import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Card, CardContent, Chip, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Snackbar,
  Alert, LinearProgress, Divider, useMediaQuery, useTheme, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon,
  Phone as PhoneIcon, Person as PersonIcon, PersonAdd as PersonAddIcon,
  CalendarMonth as CalendarIcon, Route as RouteIcon,
  Lock as LockIcon, LockOpen as LockOpenIcon, CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon, Group as GroupIcon,
  DirectionsCar as CarIcon, Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon, AccessTime as TimeIcon,
  Groups as GroupsIcon, EventNote as EventIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

// --- Types ---

interface RouteLead {
  phone: string;
  name: string;
  zone: string;
  vehicleCount: number;
  status: string;
  stopOrder: number;
  timeWindow: { from: string; to: string } | null;
  windowFormatted: string | null;
  address: string;
  addedAt?: string;
  addedBy?: string;
}

interface AvailableSlot {
  zone: string;
  driveMinutes: number;
  windowFormatted: string;
  estimatedWindow: { from: string; to: string };
}

interface InstallationRoute {
  _id: string;
  region: string;
  zone: string;
  date: string;
  capacity: number;
  departureTime: string;
  installationDuration: number;
  status: 'open' | 'closed' | 'completed';
  leads: RouteLead[];
  clients: { phone: string; name: string; addedAt?: string }[];
  createdAt?: string;
  updatedAt?: string;
}

// --- Constants ---

const REGIONS = ['Santo Domingo', 'Este', 'Cibao', 'Sur'] as const;

const REGION_COLORS: Record<string, string> = {
  'Santo Domingo': '#1976d2',
  'Cibao': '#2e7d32',
  'Sur': '#ed6c02',
  'Este': '#7b1fa2',
};

const STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'info' | 'default' | 'error'; icon: React.ReactElement }> = {
  open: { label: 'Abierta', color: 'success', icon: <LockOpenIcon fontSize="small" /> },
  closed: { label: 'Cerrada', color: 'default', icon: <LockIcon fontSize="small" /> },
  completed: { label: 'Completada', color: 'info', icon: <CheckCircleIcon fontSize="small" /> },
  cancelled: { label: 'Cancelada', color: 'error', icon: <LockIcon fontSize="small" /> },
};

const LEAD_STATUS: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' }> = {
  pending: { label: 'Pendiente', color: 'warning' },
  confirmed: { label: 'Confirmado', color: 'primary' },
  installed: { label: 'Instalado', color: 'success' },
  cancelled: { label: 'Cancelado', color: 'error' },
  no_show: { label: 'No asistió', color: 'default' },
};

function formatRouteDate(dateStr: string): string {
  if (!dateStr) return 'Sin fecha';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'Fecha inválida';
  return d.toLocaleDateString('es-DO', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function isUpcoming(dateStr: string): boolean {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const routeDate = new Date(dateStr);
  if (isNaN(routeDate.getTime())) return false;
  routeDate.setHours(0, 0, 0, 0);
  return routeDate >= today;
}

// --- Component ---

const RoutesManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [routes, setRoutes] = useState<InstallationRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ region: '', date: '', capacity: 8, departureTime: '08:30' });

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [addClientRouteId, setAddClientRouteId] = useState<string>('');
  const [addClientRegion, setAddClientRegion] = useState<string>('');
  const [clientForm, setClientForm] = useState({ name: '', phone: '', zone: '', vehicleCount: 1 });

  const [slotsOpen, setSlotsOpen] = useState<Record<string, boolean>>({});
  const [slotsData, setSlotsData] = useState<Record<string, AvailableSlot[]>>({});
  const [slotsLoading, setSlotsLoading] = useState<Record<string, boolean>>({});

  const [regionZones, setRegionZones] = useState<Record<string, string[]>>({});

  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({
    show: false, type: 'success', message: '',
  });

  // Route queue (tentative)
  const [routeQueue, setRouteQueue] = useState<any[]>([]);
  const [routeQueueSummary, setRouteQueueSummary] = useState<Record<string, number>>({});
  const [routeQueueConfig, setRouteQueueConfig] = useState<Record<string, { tentativeDate?: string }>>({});
  const [confirmQueueDialog, setConfirmQueueDialog] = useState<{ open: boolean; zone: string }>({ open: false, zone: '' });
  const [confirmQueueDate, setConfirmQueueDate] = useState('');
  const [confirmClientDialog, setConfirmClientDialog] = useState<{ open: boolean; item: any | null }>({ open: false, item: null });
  const [confirmClientForm, setConfirmClientForm] = useState({ date: '', timePreference: '' });

  // --- Fetch ---

  const fetchRouteQueue = useCallback(async () => {
    try {
      const [qRes, nRes, cRes] = await Promise.all([
        apiService.getRouteQueue(),                       // waiting + confirmed
        apiService.getRouteQueue({ status: 'notified' }), // ya notificados (ruta confirmada)
        apiService.getRouteQueueConfig(),
      ]);
      const data = qRes.data || qRes;
      const nData = nRes.data || nRes;
      const baseItems = data.items || [];
      const notifiedItems = nData.items || [];
      setRouteQueue([...baseItems, ...notifiedItems]);
      setRouteQueueSummary(data.summary || {});
      const cfgData = cRes.data || cRes;
      setRouteQueueConfig(cfgData.config || {});
    } catch {} // eslint-disable-line
  }, []);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBotRoutes();
      setRoutes(res.data || res || []);
    } catch (e) {
      console.error('Error fetching routes:', e);
      setAlert({ show: true, type: 'error', message: 'Error al cargar las rutas' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRegions = useCallback(async () => {
    try {
      const res = await apiService.getRouteRegions();
      const data = res.data || res || [];
      const map: Record<string, string[]> = {};
      data.forEach((r: { region: string; zones: string[] }) => { map[r.region] = r.zones; });
      setRegionZones(map);
    } catch (e) {
      console.error('Error fetching regions:', e);
    }
  }, []);

  useEffect(() => { fetchRoutes(); fetchRegions(); fetchRouteQueue(); }, [fetchRoutes, fetchRegions, fetchRouteQueue]);

  const fetchSlots = async (routeId: string) => {
    if (slotsOpen[routeId]) {
      setSlotsOpen(p => ({ ...p, [routeId]: false }));
      return;
    }
    setSlotsLoading(p => ({ ...p, [routeId]: true }));
    try {
      const res = await apiService.getRouteSlots(routeId);
      const data = res.data || res;
      setSlotsData(p => ({ ...p, [routeId]: data.availableSlots || [] }));
      setSlotsOpen(p => ({ ...p, [routeId]: true }));
    } catch (e) {
      console.error('Error fetching slots:', e);
      setAlert({ show: true, type: 'error', message: 'Error al cargar slots disponibles' });
    } finally {
      setSlotsLoading(p => ({ ...p, [routeId]: false }));
    }
  };

  // --- Handlers ---

  const handleCreateRoute = async () => {
    if (!createForm.region || !createForm.date) {
      setAlert({ show: true, type: 'error', message: 'Región y fecha son requeridos' });
      return;
    }
    setCreating(true);
    try {
      await apiService.createBotRoute({
        region: createForm.region,
        date: createForm.date,
        capacity: createForm.capacity,
        departureTime: createForm.departureTime,
      });
      setAlert({ show: true, type: 'success', message: 'Ruta creada exitosamente' });
      setCreateOpen(false);
      setCreateForm({ region: '', date: '', capacity: 8, departureTime: '08:30' });
      fetchRoutes();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al crear la ruta' });
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (routeId: string, newStatus: string) => {
    try {
      await apiService.updateBotRoute(routeId, { status: newStatus });
      setAlert({ show: true, type: 'success', message: `Ruta actualizada a "${STATUS_CONFIG[newStatus]?.label || newStatus}"` });
      fetchRoutes();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al actualizar la ruta' });
    }
  };

  const handleAddClient = async () => {
    if (!clientForm.name || !clientForm.phone) {
      setAlert({ show: true, type: 'error', message: 'Nombre y teléfono son requeridos' });
      return;
    }
    setAddingClient(true);
    try {
      await apiService.addClientToRoute(addClientRouteId, {
        name: clientForm.name,
        phone: clientForm.phone,
        zone: clientForm.zone,
        vehicleCount: clientForm.vehicleCount,
      });
      setAlert({ show: true, type: 'success', message: 'Cliente agregado a la ruta' });
      setAddClientOpen(false);
      setClientForm({ name: '', phone: '', zone: '', vehicleCount: 1 });
      setAddClientRouteId('');
      setSlotsOpen({});
      fetchRoutes();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al agregar cliente' });
    } finally {
      setAddingClient(false);
    }
  };

  const handleRemoveClient = async (routeId: string, phone: string) => {
    try {
      await apiService.removeClientFromRoute(routeId, phone);
      setAlert({ show: true, type: 'success', message: 'Cliente removido de la ruta' });
      setSlotsOpen({});
      fetchRoutes();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al remover cliente' });
    }
  };

  const handleConfirmQueue = async () => {
    if (!confirmQueueDialog.zone || !confirmQueueDate) return;
    try {
      // Crear la ruta; si ya existe una activa para esa zona/fecha (409), la reusamos y seguimos.
      try {
        await apiService.createBotRoute({
          region: confirmQueueDialog.zone,
          date: confirmQueueDate,
          capacity: 8,
          departureTime: '08:30',
        });
      } catch (createErr: any) {
        if (createErr.response?.status !== 409) throw createErr;
      }
      const res = await apiService.confirmRouteQueue(confirmQueueDialog.zone, confirmQueueDate);
      const notified = (res.data || res)?.notified ?? 0;
      setAlert({
        show: true,
        type: 'success',
        message: notified > 0
          ? `Ruta ${confirmQueueDialog.zone} confirmada — ${notified} cliente(s) notificado(s)`
          : `Ruta ${confirmQueueDialog.zone} confirmada (no había clientes nuevos en espera)`,
      });
      setConfirmQueueDialog({ open: false, zone: '' });
      setConfirmQueueDate('');
      fetchRoutes();
      fetchRouteQueue();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al confirmar ruta tentativa' });
    }
  };

  const handleConfirmClient = async () => {
    const item = confirmClientDialog.item;
    if (!item) return;
    try {
      await apiService.confirmRouteQueueClient(item.phone, {
        date: confirmClientForm.date || undefined,
        timePreference: confirmClientForm.timePreference || undefined,
      });
      setAlert({ show: true, type: 'success', message: `${item.name || item.phone} confirmado` });
      setConfirmClientDialog({ open: false, item: null });
      setConfirmClientForm({ date: '', timePreference: '' });
      fetchRouteQueue();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || 'Error al confirmar cliente' });
    }
  };

  const handleRemoveFromQueue = async (phone: string) => {
    try {
      await apiService.removeFromRouteQueue(phone);
      setRouteQueue(prev => prev.filter(q => q.phone !== phone));
      setAlert({ show: true, type: 'success', message: 'Cliente removido de la cola' });
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: 'Error al remover de la cola' });
    }
  };

  const openAddClientDialog = (routeId: string, region: string) => {
    setAddClientRouteId(routeId);
    setAddClientRegion(region);
    setClientForm({ name: '', phone: '', zone: '', vehicleCount: 1 });
    setAddClientOpen(true);
  };

  // --- Filtering & Sorting ---

  const filtered = routes.filter(route => {
    const routeRegion = route.region || route.zone;
    if (regionFilter && routeRegion !== regionFilter) return false;
    if (statusFilter && route.status !== statusFilter) return false;
    return true;
  });

  const upcomingRoutes = filtered
    .filter(r => isUpcoming(r.date))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const pastRoutes = filtered
    .filter(r => !isUpcoming(r.date))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // --- Stats ---

  const totalLeads = routes.reduce((sum, r) => sum + (r.leads || r.clients || []).length, 0);
  const totalVehicles = routes.reduce((sum, r) =>
    sum + (r.leads || []).reduce((s, l) => s + (l.vehicleCount || 1), 0), 0);
  const openRoutes = routes.filter(r => r.status === 'open').length;

  // --- Render Lead Row ---

  const renderLeadRow = (lead: RouteLead, routeId: string, routeStatus: string) => {
    const statusConf = LEAD_STATUS[lead.status] || LEAD_STATUS.pending;
    return (
      <Box
        key={lead.phone}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 1.5,
          mb: 0.5,
          borderRadius: 1.5,
          backgroundColor: 'action.hover',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.selected' },
        }}
        onClick={() => navigate(`/bot/leads?phone=${lead.phone}`)}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
          <PersonIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ '&:hover': { textDecoration: 'underline' } }}>{lead.name || 'Sin nombre'}</Typography>
              <Chip label={statusConf.label} color={statusConf.color} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.3, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                component="a"
                href={`tel:${lead.phone}`}
                sx={{ color: 'success.main', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <PhoneIcon sx={{ fontSize: 11 }} />
                {lead.phone}
              </Typography>
              {lead.zone && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <LocationIcon sx={{ fontSize: 11 }} />
                  {lead.zone}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <CarIcon sx={{ fontSize: 11 }} />
                {lead.vehicleCount || 1} veh.
              </Typography>
            </Box>
            {lead.windowFormatted && (
              <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                <TimeIcon sx={{ fontSize: 12 }} />
                {lead.windowFormatted}
              </Typography>
            )}
          </Box>
        </Box>
        {routeStatus !== 'completed' && (
          <Tooltip title="Remover cliente">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); handleRemoveClient(routeId, lead.phone); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // --- Render Route Card ---

  const renderRouteCard = (route: InstallationRoute) => {
    const leads = route.leads || [];
    const activeLeads = leads.filter(l => l.status !== 'cancelled');
    const capacityPercent = route.capacity > 0 ? (activeLeads.length / route.capacity) * 100 : 0;
    const isFull = activeLeads.length >= route.capacity;
    const statusConf = STATUS_CONFIG[route.status] || STATUS_CONFIG.open;
    const upcoming = isUpcoming(route.date);
    // Vencida = abierta pero la fecha ya pasó (el cron la cerrará; se muestra el aviso mientras tanto)
    const isExpiredOpen = route.status === 'open' && !upcoming;
    // Solo una ruta abierta, futura y no llena acepta nuevos clientes
    const canAddClients = route.status === 'open' && upcoming && !isFull;
    const region = route.region || route.zone;
    const routeSlots = slotsData[route._id] || [];
    const isExpanded = slotsOpen[route._id] || false;

    return (
      <Grid size={{ xs: 12, md: 6 }} key={route._id}>
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            borderLeft: '4px solid',
            borderLeftColor: REGION_COLORS[region] || '#757575',
          }}
        >
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={region}
                  size="small"
                  sx={{ backgroundColor: REGION_COLORS[region] || '#757575', color: 'white', fontWeight: 600 }}
                />
                <Chip
                  icon={<ScheduleIcon fontSize="small" />}
                  label={`Sale ${route.departureTime || '08:30'}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isExpiredOpen && (
                  <Chip label="Vencida" size="small" color="error" sx={{ fontWeight: 600 }} />
                )}
                <Chip icon={statusConf.icon} label={statusConf.label} size="small" color={statusConf.color} variant="outlined" />
              </Box>
            </Box>

            {/* Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body1" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                {formatRouteDate(route.date)}
              </Typography>
            </Box>

            {/* Capacity + vehicles */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <GroupIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">Capacidad</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <CarIcon sx={{ fontSize: 14 }} />
                    {leads.reduce((s, l) => s + (l.vehicleCount || 1), 0)} veh.
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color={isFull ? 'error.main' : 'text.primary'}>
                    {activeLeads.length} / {route.capacity}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(capacityPercent, 100)}
                sx={{
                  height: 8, borderRadius: 4, backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: isFull ? 'error.main' : capacityPercent >= 80 ? 'warning.main' : 'success.main',
                  },
                }}
              />
            </Box>

            {/* Leads list */}
            {leads.length > 0 ? (
              <>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                  Clientes asignados ({leads.length})
                </Typography>
                {leads
                  .sort((a, b) => (a.stopOrder || 0) - (b.stopOrder || 0))
                  .map((lead) => renderLeadRow(lead, route._id, route.status))}
              </>
            ) : (
              <>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 1 }}>
                  Sin clientes asignados
                </Typography>
              </>
            )}

            {/* Available slots toggle */}
            {canAddClients && (
              <>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => fetchSlots(route._id)}
                  endIcon={slotsLoading[route._id] ? <CircularProgress size={14} /> : isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mt: 1, mb: 0.5, textTransform: 'none', color: 'text.secondary' }}
                >
                  Slots disponibles
                </Button>
                <Collapse in={isExpanded}>
                  {routeSlots.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 250, mt: 0.5 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Zona</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Viaje</TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 600 }}>Ventana estimada</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {routeSlots.map((slot) => (
                            <TableRow key={slot.zone} hover>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{slot.zone}</TableCell>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>{slot.driveMinutes} min</TableCell>
                              <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 500 }}>{slot.windowFormatted}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
                      No hay slots disponibles
                    </Typography>
                  )}
                </Collapse>
              </>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {canAddClients && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => openAddClientDialog(route._id, region)}
                >
                  Agregar Cliente
                </Button>
              )}
              {route.status === 'open' && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<LockIcon />}
                  onClick={() => handleUpdateStatus(route._id, 'closed')}
                >
                  Cerrar
                </Button>
              )}
              {route.status === 'closed' && (
                <>
                  {/* Solo se puede reabrir una ruta cuya fecha aún no ha pasado */}
                  {upcoming && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<LockOpenIcon />}
                      onClick={() => handleUpdateStatus(route._id, 'open')}
                    >
                      Reabrir
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handleUpdateStatus(route._id, 'completed')}
                  >
                    Completar
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
            Rutas de Instalación
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {routes.length} ruta(s) · {openRoutes} abierta(s) · {totalLeads} cliente(s) · {totalVehicles} vehículo(s)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchRoutes} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateForm({ region: '', date: '', capacity: 8, departureTime: '08:30' });
              setCreateOpen(true);
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            Nueva Ruta
          </Button>
        </Box>
      </Box>

      {/* Region Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip
          label="Todas"
          onClick={() => setRegionFilter('')}
          color={regionFilter === '' ? 'primary' : 'default'}
          variant={regionFilter === '' ? 'filled' : 'outlined'}
          size="small"
        />
        {REGIONS.map(region => (
          <Chip
            key={region}
            label={region}
            onClick={() => setRegionFilter(regionFilter === region ? '' : region)}
            variant={regionFilter === region ? 'filled' : 'outlined'}
            size="small"
            sx={
              regionFilter === region
                ? { backgroundColor: REGION_COLORS[region], color: 'white' }
                : { borderColor: REGION_COLORS[region], color: REGION_COLORS[region] }
            }
          />
        ))}
      </Box>

      {/* Status Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label="Todas"
          onClick={() => setStatusFilter('')}
          color={statusFilter === '' ? 'primary' : 'default'}
          variant={statusFilter === '' ? 'filled' : 'outlined'}
          size="small"
        />
        {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
          <Chip
            key={key}
            icon={conf.icon}
            label={conf.label}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            color={statusFilter === key ? conf.color : 'default'}
            variant={statusFilter === key ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RouteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {routes.length === 0
              ? 'No hay rutas creadas aún. Crea la primera con el botón "Nueva Ruta".'
              : 'No hay rutas que coincidan con los filtros seleccionados.'}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tentative Routes from Queue */}
          {routeQueue.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon color="warning" />
                Cola de rutas por zona
                <Chip size="small" label={`${routeQueue.filter(q => q.status === 'waiting').length} en espera`} color="warning" sx={{ fontWeight: 600 }} />
                {routeQueue.filter(q => q.status === 'notified' || q.status === 'confirmed').length > 0 && (
                  <Chip size="small" icon={<CheckCircleIcon fontSize="small" />}
                    label={`${routeQueue.filter(q => q.status === 'notified' || q.status === 'confirmed').length} en ruta confirmada`}
                    color="success" sx={{ fontWeight: 600 }} />
                )}
              </Typography>
              <Grid container spacing={2}>
                {(['Cibao', 'Sur', 'Este'] as const).map(zone => {
                  const waitingItems = routeQueue.filter(q => (q.routeZone || q.zone) === zone && q.status === 'waiting');
                  const confirmedItems = routeQueue.filter(q => (q.routeZone || q.zone) === zone && q.status === 'confirmed');
                  const notifiedItems = routeQueue.filter(q => (q.routeZone || q.zone) === zone && q.status === 'notified');
                  if (waitingItems.length === 0 && confirmedItems.length === 0 && notifiedItems.length === 0) return null;
                  const totalItems = waitingItems.length + confirmedItems.length + notifiedItems.length;
                  // La ruta ya está confirmada si hay gente notificada (se les avisó la fecha) o confirmada.
                  const routeConfirmed = notifiedItems.length > 0 || confirmedItems.length > 0;
                  // Fecha real de la ruta confirmada (viene en los notificados como routeDate).
                  const routeDate = notifiedItems.find(i => i.routeDate)?.routeDate
                    || confirmedItems.find(i => i.confirmDate)?.confirmDate
                    || routeQueueConfig[zone]?.tentativeDate || null;
                  const zoneColor = REGION_COLORS[zone] || '#757575';

                  const renderQueueClient = (item: any, isConfirmed: boolean) => (
                    <Box key={item.phone} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      py: 0.8, px: 1.5, mb: 0.5, borderRadius: 1.5,
                      backgroundColor: isConfirmed ? 'success.50' : 'action.hover',
                      border: isConfirmed ? '1px solid' : 'none',
                      borderColor: isConfirmed ? 'success.200' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: isConfirmed ? 'success.100' : 'action.selected' },
                    }}
                    onClick={() => navigate(`/bot/leads?phone=${item.phone}`)}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0, flex: 1 }}>
                        {isConfirmed
                          ? <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.3 }} />
                          : <PersonIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ '&:hover': { textDecoration: 'underline' } }}>{item.name || 'Sin nombre'}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.3, flexWrap: 'wrap' }}>
                            <Typography variant="caption" component="a" href={`tel:${item.phone}`}
                              sx={{ color: 'success.main', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 11 }} />
                              {item.phone}
                            </Typography>
                            {(item.preferredDay || item.confirmDate) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <EventIcon sx={{ fontSize: 11 }} />
                                {item.confirmDate ? new Date(item.confirmDate + 'T12:00').toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' }) : item.preferredDay}
                              </Typography>
                            )}
                            {(item.preferredTime || item.timePreference) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <TimeIcon sx={{ fontSize: 11 }} />
                                {item.timePreference || item.preferredTime}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        {!isConfirmed && (
                          <Tooltip title="Confirmar cliente">
                            <IconButton size="small" color="success" onClick={() => {
                              setConfirmClientDialog({ open: true, item });
                              setConfirmClientForm({ date: routeQueueConfig[zone]?.tentativeDate || '', timePreference: item.preferredTime || '' });
                            }}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remover de cola">
                          <IconButton size="small" color="error" onClick={() => handleRemoveFromQueue(item.phone)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );

                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={`queue-${zone}`}>
                      <Card variant="outlined" sx={{ borderLeft: '4px solid', borderLeftColor: zoneColor, borderStyle: 'dashed', borderWidth: '1px 1px 1px 4px' }}>
                        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={zone} size="small" sx={{ backgroundColor: zoneColor, color: 'white', fontWeight: 600 }} />
                              <Chip icon={<GroupsIcon fontSize="small" />} label={`${totalItems} persona${totalItems !== 1 ? 's' : ''}`}
                                size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                              {confirmedItems.length > 0 && (
                                <Chip icon={<CheckCircleIcon fontSize="small" />} label={`${confirmedItems.length} confirmado${confirmedItems.length !== 1 ? 's' : ''}`}
                                  size="small" color="success" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                              )}
                            </Box>
                            {routeConfirmed ? (
                              <Chip icon={<CheckCircleIcon fontSize="small" />} label="Ruta confirmada" size="small" color="success" sx={{ fontWeight: 600 }} />
                            ) : (
                              <Chip label="Tentativa" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
                            )}
                          </Box>
                          {/* Date picker: tentativa si no confirmada, fecha confirmada si ya se confirmó */}
                          {routeConfirmed ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <CalendarIcon fontSize="small" color="success" />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                Ruta confirmada{routeDate ? ` para el ${new Date((typeof routeDate === 'string' ? routeDate.slice(0,10) : routeDate) + 'T12:00').toLocaleDateString('es-DO', { weekday: 'long', day: 'numeric', month: 'long' })}` : ''}
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <CalendarIcon fontSize="small" color="action" />
                              <TextField
                                size="small" type="date" label="Fecha tentativa"
                                value={routeQueueConfig[zone]?.tentativeDate || ''}
                                onChange={async (e) => {
                                  const val = e.target.value || null;
                                  setRouteQueueConfig(prev => ({ ...prev, [zone]: { ...prev[zone], tentativeDate: val || undefined } }));
                                  try {
                                    await apiService.setRouteQueueConfig(zone, val);
                                    setAlert({ show: true, type: 'success', message: val ? `Fecha tentativa ${zone}: ${val}` : `Fecha tentativa ${zone} removida` });
                                  } catch {
                                    setAlert({ show: true, type: 'error', message: 'Error al guardar fecha' });
                                  }
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                              />
                              {routeQueueConfig[zone]?.tentativeDate && (
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', whiteSpace: 'nowrap' }}>
                                  {new Date(routeQueueConfig[zone].tentativeDate + 'T12:00').toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {!routeConfirmed && totalItems < 3 && (
                            <Alert severity="info" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                              Faltan {3 - totalItems} persona(s) para confirmar ruta (mínimo 3)
                            </Alert>
                          )}
                          {!routeConfirmed && totalItems >= 3 && (
                            <Alert severity="success" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                              Suficientes personas para confirmar esta ruta
                            </Alert>
                          )}

                          {/* Notified clients — ya en la ruta confirmada */}
                          {notifiedItems.length > 0 && (
                            <>
                              <Divider sx={{ mb: 1 }} />
                              <Typography variant="body2" fontWeight={600} color="success.main" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                                En la ruta ({notifiedItems.length}) — notificados
                              </Typography>
                              {notifiedItems.map((item: any) => renderQueueClient(item, true))}
                            </>
                          )}

                          {/* Confirmed clients */}
                          {confirmedItems.length > 0 && (
                            <>
                              <Divider sx={{ mb: 1, mt: notifiedItems.length > 0 ? 1.5 : 0 }} />
                              <Typography variant="body2" fontWeight={600} color="success.main" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                                Confirmados ({confirmedItems.length})
                              </Typography>
                              {confirmedItems.map((item: any) => renderQueueClient(item, true))}
                            </>
                          )}

                          {/* Waiting clients */}
                          {waitingItems.length > 0 && (
                            <>
                              <Divider sx={{ mb: 1, mt: (notifiedItems.length > 0 || confirmedItems.length > 0) ? 1.5 : 0 }} />
                              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                {routeConfirmed ? `Nuevos interesados (${waitingItems.length}) — sin notificar` : `En espera (${waitingItems.length})`}
                              </Typography>
                              {waitingItems.map((item: any) => renderQueueClient(item, false))}
                            </>
                          )}

                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button size="small" variant="contained" color="success"
                              startIcon={<CheckCircleIcon />}
                              disabled={routeConfirmed ? waitingItems.length === 0 : totalItems < 3}
                              onClick={() => { setConfirmQueueDialog({ open: true, zone }); setConfirmQueueDate(routeConfirmed && routeDate ? (typeof routeDate === 'string' ? routeDate.slice(0,10) : '') : ''); }}>
                              {routeConfirmed ? 'Notificar nuevos' : 'Confirmar Ruta'}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {upcomingRoutes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                Próximas rutas ({upcomingRoutes.length})
              </Typography>
              <Grid container spacing={2}>
                {upcomingRoutes.map(renderRouteCard)}
              </Grid>
            </Box>
          )}

          {pastRoutes.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <CheckCircleIcon color="action" />
                Rutas pasadas ({pastRoutes.length})
              </Typography>
              <Grid container spacing={2}>
                {pastRoutes.map(renderRouteCard)}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* ===== CREATE ROUTE DIALOG ===== */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nueva Ruta
          <IconButton onClick={() => setCreateOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Región</InputLabel>
              <Select
                value={createForm.region}
                label="Región"
                onChange={e => setCreateForm(p => ({ ...p, region: e.target.value }))}
              >
                {REGIONS.map(region => (
                  <MenuItem key={region} value={region}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: REGION_COLORS[region] }} />
                      {region}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth required label="Fecha" type="date"
              value={createForm.date}
              onChange={e => setCreateForm(p => ({ ...p, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              helperText="No se permiten fechas pasadas"
            />
            <TextField
              fullWidth label="Hora de salida" type="time"
              value={createForm.departureTime}
              onChange={e => setCreateForm(p => ({ ...p, departureTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Hora de salida desde Santo Domingo"
            />
            <TextField
              fullWidth label="Capacidad máxima" type="number"
              value={createForm.capacity}
              onChange={e => setCreateForm(p => ({ ...p, capacity: Math.max(1, parseInt(e.target.value) || 1) }))}
              inputProps={{ min: 1, max: 50 }}
              helperText="Cantidad máxima de clientes para esta ruta"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateRoute}
            disabled={creating || !createForm.region || !createForm.date}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? 'Creando...' : 'Crear Ruta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== ADD CLIENT DIALOG ===== */}
      <Dialog open={addClientOpen} onClose={() => setAddClientOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Agregar Cliente a Ruta
          <IconButton onClick={() => setAddClientOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth required label="Nombre del Cliente"
              value={clientForm.name}
              onChange={e => setClientForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Juan Pérez"
            />
            <TextField
              fullWidth required label="Teléfono"
              value={clientForm.phone}
              onChange={e => setClientForm(p => ({ ...p, phone: e.target.value }))}
              placeholder="809-555-1234"
              helperText="Número de WhatsApp del cliente"
            />
            <FormControl fullWidth>
              <InputLabel>Zona / Ciudad</InputLabel>
              <Select
                value={clientForm.zone}
                label="Zona / Ciudad"
                onChange={e => setClientForm(p => ({ ...p, zone: e.target.value }))}
              >
                <MenuItem value="">Sin especificar</MenuItem>
                {(regionZones[addClientRegion] || []).map(zone => (
                  <MenuItem key={zone} value={zone}>{zone}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth label="Cantidad de vehículos" type="number"
              value={clientForm.vehicleCount}
              onChange={e => setClientForm(p => ({ ...p, vehicleCount: Math.max(1, parseInt(e.target.value) || 1) }))}
              inputProps={{ min: 1, max: 20 }}
              helperText="Cada vehículo añade ~35 min de instalación"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddClientOpen(false)} disabled={addingClient}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAddClient}
            disabled={addingClient || !clientForm.name || !clientForm.phone}
            startIcon={addingClient ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {addingClient ? 'Agregando...' : 'Agregar Cliente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm tentative route dialog */}
      <Dialog open={confirmQueueDialog.open} onClose={() => setConfirmQueueDialog({ open: false, zone: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar Ruta Tentativa — {confirmQueueDialog.zone}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Se creará una ruta de instalación para {confirmQueueDialog.zone} y se notificará a todos los clientes en espera por WhatsApp.
            </Typography>
            <TextField label="Fecha de la ruta" type="date" fullWidth value={confirmQueueDate}
              onChange={(e) => setConfirmQueueDate(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              helperText="Selecciona un sábado o domingo para mayor disponibilidad" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmQueueDialog({ open: false, zone: '' })}>Cancelar</Button>
          <Button variant="contained" color="success" disabled={!confirmQueueDate}
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirmQueue}>
            Crear Ruta y Notificar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm individual client dialog */}
      <Dialog open={confirmClientDialog.open} onClose={() => setConfirmClientDialog({ open: false, item: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar Cliente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {confirmClientDialog.item && (
              <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                <Typography variant="body2" fontWeight={600}>{confirmClientDialog.item.name || 'Sin nombre'}</Typography>
                <Typography variant="caption" color="text.secondary">{confirmClientDialog.item.phone} — {confirmClientDialog.item.routeZone || confirmClientDialog.item.zone}</Typography>
              </Box>
            )}
            <TextField label="Fecha de instalación" type="date" fullWidth value={confirmClientForm.date}
              onChange={(e) => setConfirmClientForm(p => ({ ...p, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }} />
            <FormControl fullWidth>
              <InputLabel>Horario preferido</InputLabel>
              <Select value={confirmClientForm.timePreference} label="Horario preferido"
                onChange={(e) => setConfirmClientForm(p => ({ ...p, timePreference: e.target.value }))}>
                <MenuItem value="">Sin especificar</MenuItem>
                <MenuItem value="mañana">Mañana (8:00 - 12:00)</MenuItem>
                <MenuItem value="tarde">Tarde (1:00 - 5:00)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmClientDialog({ open: false, item: null })}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
            onClick={handleConfirmClient}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={alert.show} autoHideDuration={5000} onClose={() => setAlert(p => ({ ...p, show: false }))}>
        <Alert severity={alert.type} onClose={() => setAlert(p => ({ ...p, show: false }))} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoutesManagementPage;
